import { useState } from "react";
import { Users, FileText, TrendingUp, Receipt, Ticket, FileSpreadsheet, Loader2 } from "lucide-react";
import { useGetStaffPerformanceReport, useDownloadStaffSalesPerformanceExcel } from "../../service/reportsService";
import { useGetAllBranches } from "../../service/branchService";
import Loading from "../../components/common/LoaderCommon";
import PaginationComponent from "../../components/PaginationComponent";
import { cn } from "../../utils/cn";

const isAdmin = () =>
  (localStorage.getItem("role") || "").toLowerCase() === "admin";

const fmt = (val: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(val ?? 0);

const inputCls =
  "h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";

function StaffPerformanceReport() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState(
    isAdmin() ? "" : localStorage.getItem("branchId") || "",
  );
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const canFetch = !!startDate && !!endDate && !!selectedBranch;

  const { data: branchData } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const { data, isLoading } = useGetStaffPerformanceReport(
    {
      branchId: Number(selectedBranch),
      startDate,
      endDate,
      page,
      size: pageSize,
    },
    canFetch,
  );

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  const { mutate: downloadExcel, isPending: isDownloading } = useDownloadStaffSalesPerformanceExcel();

  const handleExport = () => {
    if (!canFetch) return;
    downloadExcel({
      branchId: Number(selectedBranch),
      startDate,
      endDate,
      page: 0,
      size: 1000,
    });
  };

  return (
    <div className="space-y-5">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex flex-wrap items-end gap-4">
          {/* Start date */}
          <div>
            <label className={labelCls}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              className={inputCls}
            />
          </div>

          {/* End date */}
          <div>
            <label className={labelCls}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              className={inputCls}
            />
          </div>

          {/* Branch */}
          {isAdmin() ? (
            <div>
              <label className={labelCls}>Branch</label>
              <select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setPage(0);
                }}
                className={cn(inputCls, "pr-8")}
              >
                <option value="">Select branch</option>
                {branchData?.content.map((b) => (
                  <option key={b.branchId} value={String(b.branchId)}>
                    {b.branchName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className={labelCls}>Branch</label>
              <input
                type="text"
                value={
                  branchData?.content.find(
                    (b) => String(b.branchId) === selectedBranch,
                  )?.branchName || selectedBranch
                }
                disabled
                className={cn(inputCls, "cursor-not-allowed opacity-60")}
              />
            </div>
          )}

          <div className="ml-auto flex items-center">
            <button
              type="button"
              disabled={!canFetch || isDownloading}
              onClick={handleExport}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {!canFetch ? (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] py-16 text-center">
          <FileText className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
          <p className="text-sm font-medium text-[#52627C]">
            Select a date range{isAdmin() ? " and branch" : ""} to view staff
            performance
          </p>
        </div>
      ) : isLoading ? (
        <Loading />
      ) : (
        <>
          KPI summary row
          {/* {rows.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <KpiCard
                label="Total Staff"
                value={String(data?.totalElements ?? 0)}
                icon={<Users className="w-5 h-5" />}
                color="blue"
              />
              <KpiCard
                label="Total Sales"
                value={fmt(rows.reduce((s, r) => s + r.totalSales, 0))}
                icon={<TrendingUp className="w-5 h-5" />}
                color="green"
              />
              <KpiCard
                label="Total Invoices"
                value={String(rows.reduce((s, r) => s + r.totalInvoice, 0))}
                icon={<Receipt className="w-5 h-5" />}
                color="orange"
              />
            </div>
          )} */}
          {/* Table card */}
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EEF9]">
              <p className="text-sm text-[#52627C]">
                <span className="font-semibold text-[#21314A]">
                  {data?.totalElements ?? 0}
                </span>{" "}
                staff members found
              </p>
              <PaginationComponent
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>

            {rows.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
                <p className="text-sm text-[#52627C]">
                  No staff performance data for this period
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                      <th className="px-6 py-3">Sl.</th>
                      <th className="px-6 py-3">Employee</th>
                      <th className="px-6 py-3 text-right">Total Sales</th>
                      <th className="px-6 py-3 text-right">Invoices</th>
                      <th className="px-6 py-3 text-right">Avg Ticket</th>
                      <th className="px-6 py-3 text-right">Profit Generated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={row.staffId}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                      >
                        <td className="px-6 py-4 text-[#8A9BB5]">
                          {page * pageSize + index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#EEF3FF] flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-[#1A5EF9]">
                                {row.employeeName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-[#1A2B4A]">
                              {row.employeeName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-[#1A2B4A]">
                          {fmt(row.totalSales)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex items-center justify-center rounded-full bg-[#EEF3FF] text-[#1A5EF9] text-xs font-semibold px-2.5 py-1 min-w-[32px]">
                            {row.totalInvoice}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-[#52627C]">
                          {fmt(row.avgTicket)}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-[#0E9F6E]">
                          {fmt(row.profitGenerated)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────

type KpiColor = "blue" | "green" | "orange";

const kpiColors: Record<KpiColor, { bg: string; icon: string; value: string }> =
  {
    blue: {
      bg: "bg-[#EEF3FF]",
      icon: "text-[#1A5EF9]",
      value: "text-[#1A5EF9]",
    },
    green: {
      bg: "bg-[#E6F9F0]",
      icon: "text-[#1A9E5C]",
      value: "text-[#1A9E5C]",
    },
    orange: {
      bg: "bg-[#FFF7ED]",
      icon: "text-[#E08A1E]",
      value: "text-[#E08A1E]",
    },
  };

function KpiCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: KpiColor;
}) {
  const c = kpiColors[color];
  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_8px_24px_rgba(26,94,249,0.06)] p-5">
      <div
        className={cn(
          "w-10 h-10 rounded-[12px] flex items-center justify-center mb-3",
          c.bg,
        )}
      >
        <span className={c.icon}>{icon}</span>
      </div>
      <p className="text-xs font-medium text-[#8A9BB5] mb-1">{label}</p>
      <p className={cn("text-xl font-bold", c.value)}>{value}</p>
    </div>
  );
}

export default StaffPerformanceReport;
